import { ReactElement } from 'react';

import { useApiMutation, useApiRequest } from '~/hooks/api';
import Loader from '~/ui/components/common/Loader';
import AiSettingsForm from '~/ui/pages/AiManagement/components/AiSettingsForm';
import TestAiRequest from '~/ui/pages/AiManagement/components/TestAiRequest';

import { useStoreActions, useStoreState } from '~/store/hooks';

import { IAiSettingsFormValues } from '~/ui/pages/AiManagement/types';

const AiClinicSettings = (): ReactElement => {
  const { onGetAiClinicSettings, onGetAiModels, onUpdateAiClinicSettings, onGetAiSystemSettings } =
    useStoreActions(actions => actions.ai);
  const { currentClinic, currentSystem, aiModels } = useStoreState(store => store.ai);
  const { current } = useStoreState(store => store.clinic);
  const clinicId = current?.id;

  const { isLoading: loadingClinic, refetch: refetchClinic } = useApiRequest(
    () => onGetAiClinicSettings(clinicId),
    {
      enabled: !!clinicId,
      dependencies: [clinicId],
    },
  );
  const { isLoading: loadingSystemAndModels } = useApiRequest([
    () => onGetAiModels(),
    () => onGetAiSystemSettings(),
  ]);

  const { mutate: saveAiSettings, isLoading: saving } = useApiMutation(
    (values: IAiSettingsFormValues) =>
      onUpdateAiClinicSettings({
        ...values,
        maxTokens: Number(values.maxTokens),
        clinicId: current.id,
        speechToTextModelName:
          // Speech to text is hidden for now, for the backend we send first option as default, because this value is required on the backend side
          values.speechToTextModelName || aiModels?.speechToTextModels?.[0] || '',
      }),
    {
      notifyMessage: 'AI settings successfully updated',
      onSuccess: refetchClinic,
    },
  );

  const isLoading = loadingClinic || loadingSystemAndModels;
  const aiSettings = currentClinic || currentSystem;

  return isLoading ? (
    <Loader />
  ) : (
    <>
      <AiSettingsForm
        title="AI Settings"
        onSubmit={saveAiSettings}
        defaultValues={aiSettings}
        aiModels={aiModels}
        loading={saving}
      />
      {aiSettings && <TestAiRequest aiSettings={aiSettings} />}
    </>
  );
};

export default AiClinicSettings;
